import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Banknote, FilterX, HandCoins, Plus, Repeat, Upload, X } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Indicador, Selo, ValorPorMoeda } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Selecao } from '../../componentes/formulario';
import { aceitoDaUrl, ChipsFixos, ChipsValor, contarFiltros, faixaDaUrl, FiltroParceiros, FiltroPeriodo, FiltroValor, FiltroVarios, LinhaChips, MaisFiltros, PeriodoComBase, periodoDaUrl, uuidsDaUrl, limparFiltrosEFocarBusca, type ParteFiltro } from '../../componentes/filtros-lista';
import { filtrosNaUrl } from '../../componentes/filtros-visao';
import { Tabela, type Coluna, type SelecaoTabela } from '../../componentes/tabela';
import { get, getTodos } from '../../nucleo/api';
import { data, dinheiro, dinheiroPorMoeda, nomeParceiro, somaPorMoeda, textoLivre } from '../../nucleo/formato';
import { descricaoGerada } from '../../nucleo/texto-sistema';
import { useSessao } from '../../nucleo/sessao';
import { AplicarCredito } from './AplicarCredito';
import { BaixaLote } from './BaixaLote';
import { DetalheDocumento } from './DetalheDocumento';
import { FormDocumento } from './FormDocumento';
import { ImportarTitulos, caminhoImportarTitulos } from './ImportarTitulos';
import { BotaoModeloCsv } from '../../componentes/importacao';
import { Renegociar, renegociaveisJuntos } from './Renegociar';
import { caminhoDaCategoria, useCategoriasFin, useVerFinanceiro } from './dados';
import { COR_ESTADO, type CentroFin, type ContaFin, type Documento, type Pagina, type TipoDocumento, type Totais } from './tipos';
import { anunciarOcultos } from '../../componentes/sensivel';

/** Estados da URL que a tela entende: os grupos da `Selecao` e cada estado da API (review r27 spec, B1). */
const ESTADOS_URL = ['abertos', 'todos', 'em_aberto', 'parcial', 'pago', 'cancelado', 'perdido', 'renegociado'] as const;

/** C1: cards que recortam a lista (o "Total do período" é o sem recorte). Na URL, `?card=`; vira `janela` na API. */
export const CARDS_DOCUMENTOS = ['vencidos', 'hoje', 'a_vencer', 'liquidados'] as const;
export type CardDocumentos = (typeof CARDS_DOCUMENTOS)[number];

/**
 * C1: card ativo pela URL. O `?atrasados=true` dos links de antes (Início > Maiores devedores, ficha do parceiro) equivale ao
 * card Vencidos; `?card=` vale mais. Valor fora da lista é ignorado (como os outros filtros da URL).
 */
export function cardDaUrl(card: string | null | undefined, atrasados: string | null | undefined): CardDocumentos | '' {
  return (aceitoDaUrl(card, CARDS_DOCUMENTOS) as CardDocumentos | '') || (atrasados === 'true' ? 'vencidos' : '');
}

/** C1: clicar no card ativo volta ao Total (sem recorte); clicar em outro troca o recorte e tira o `atrasados` antigo. */
export function urlDoCard(atual: CardDocumentos | '', clicado: CardDocumentos | ''): { card: string; atrasados: string } {
  return { card: clicado === atual ? '' : clicado, atrasados: '' };
}

/**
 * Lista de documentos de uma aba (M5.7): a receber (fatura), a pagar (conta a pagar e reembolso) ou notas de crédito.
 * Filtros ficam na URL, então o link da ficha do parceiro chega já filtrado. Atrasados aparecem em laranja.
 */
export function ListaDocumentos({ aba, tipos }: { aba: 'a-receber' | 'a-pagar' | 'notas-credito'; tipos: TipoDocumento[] }) {
  const { t } = useTranslation('contas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const [busca, setBusca] = useSearchParams();
  const [novo, setNovo] = useState(false);
  const [aberto, setAberto] = useState<string | null>(null);
  const [importar, setImportar] = useState(false);
  // Fase 2 (W14): documentos escolhidos na lista para a baixa em lote e a renegociação (guardados com a linha, para o total).
  const [selecaoBruta, setSelecaoBruta] = useState<{ chave: string; mapa: Map<string, Documento> }>(() => ({ chave: '', mapa: new Map() }));
  const [loteAberto, setLoteAberto] = useState(false);
  const [renegAberto, setRenegAberto] = useState(false);
  // Fase 2 (W34): nota de crédito escolhida na lista para aplicar em fatura.
  const [aplicarNota, setAplicarNota] = useState<Documento | null>(null);

  const filtro = (k: string) => busca.get(k) ?? '';
  const mudar = (k: string, v: string) => mudarVarios({ [k]: v });
  const mudarVarios = (parcial: Record<string, string>) => setBusca((atual) => filtrosNaUrl(atual, parcial), { replace: true });
  // Padrão da tela: só o que está em aberto ou parcial (o que precisa de ação). "todos" mostra também pagos e cancelados.
  // Review r27 spec (B1): valor que a API recusaria (`?estado=xx`, id que não é uuid, data ou centavos inválidos) é ignorado,
  // como nas Movimentações (r26 robustez O6); antes ia cru, a API dava 422 e a lista e os indicadores ficavam "Indisponível".
  const estadoUrl = aceitoDaUrl(filtro('estado'), ESTADOS_URL) || 'abertos';
  // Review r28 spec (B1): o par de datas só vale junto (o mesmo no `FiltroPeriodo` e na API).
  const periodo = periodoDaUrl(filtro('de'), filtro('ate'));
  // C1: A receber e A pagar ganham os 5 cards clicáveis e o "Mais filtros" em painel; Notas de crédito fica como estava.
  const comCards = aba !== 'notas-credito';
  const card = comCards ? cardDaUrl(filtro('card'), filtro('atrasados')) : '';
  const params = useMemo(
    () => ({
      tipo: tipos.join(','),
      // C1: com um card ativo, o padrão "Em aberto e parciais" não vale (o card é o recorte: Recebidos traz os pagos);
      // um estado escolhido no painel continua valendo junto.
      estado: estadoUrl === 'todos' || (card && estadoUrl === 'abertos') ? undefined : estadoUrl === 'abertos' ? 'em_aberto,parcial' : estadoUrl,
      atrasados: comCards ? undefined : aceitoDaUrl(filtro('atrasados'), ['true', 'false']) || undefined,
      janela: card || undefined,
      // C1: contas financeiras pela conta onde a baixa caiu.
      conta_id: comCards ? uuidsDaUrl(filtro('conta_id')).join(',') || undefined : undefined,
      parceiro_id: uuidsDaUrl(filtro('parceiro_id')).join(',') || undefined,
      // Fase 2 (W36): período por vencimento (padrão) ou competência, categoria, centro de custo e faixa de valor.
      campo_data: filtro('campo_data') === 'competencia' ? 'competencia' : undefined,
      de: periodo.de || undefined,
      ate: periodo.ate || undefined,
      // Review r25 spec (B1): categorias e centros em lista (vírgula), como o antigo.
      categoria_id: uuidsDaUrl(filtro('categoria_id')).join(',') || undefined,
      centro_custo_id: uuidsDaUrl(filtro('centro_custo_id')).join(',') || undefined,
      ...faixaDaUrl(filtro('valor_min_centavos'), filtro('valor_max_centavos')),
    }),
    [busca, tipos, aba], // eslint-disable-line react-hooks/exhaustive-deps
  );
  // Os filtros que os totais seguem (todos, menos o estado e o card): a frase do card Total diz se há algum.
  const filtrandoTotais = !!(params.parceiro_id || params.conta_id || params.de || params.ate || params.categoria_id || params.centro_custo_id || params.valor_min_centavos || params.valor_max_centavos);
  const filtrando = !!(estadoUrl !== 'abertos' || params.atrasados || card || filtrandoTotais);
  // Trocar o filtro troca as linhas: a seleção feita com outro filtro não vale mais.
  const chaveFiltro = JSON.stringify(params);
  const marcados = useMemo(() => (selecaoBruta.chave === chaveFiltro ? selecaoBruta.mapa : new Map<string, Documento>()), [selecaoBruta, chaveFiltro]);
  const setMarcados = (f: Map<string, Documento> | ((m: Map<string, Documento>) => Map<string, Documento>)) =>
    setSelecaoBruta((atual) => {
      const base = atual.chave === chaveFiltro ? atual.mapa : new Map<string, Documento>();
      return { chave: chaveFiltro, mapa: typeof f === 'function' ? f(base) : f };
    });
  // Os totais mostram o que está em aberto e atrasado, então não seguem o filtro de estado da lista.
  // C1: nem o card ativo (cada card é um recorte dos mesmos totais).
  const paramsTotais = useMemo(() => ({ ...params, estado: undefined, janela: undefined }), [params]);
  const totais = useQuery({ queryKey: ['/contas/documentos', 'totais', paramsTotais], queryFn: () => get<Totais>('/contas/documentos/totais', paramsTotais) });
  const moeda = s.workspace.moeda;
  // Lista de 04/11, item 3: cada total por moeda, nunca somando moedas (o título em dólar do contrato da Malgo International).
  const totalEm = (campo: 'em_aberto_centavos' | 'atrasado_centavos' | 'vence_em_7_dias_centavos' | 'vence_hoje_centavos' | 'a_vencer_centavos' | 'liquidado_centavos' | 'total_centavos') => {
    const d = totais.data;
    if (!d) return '...';
    const linhas = (d.por_moeda ?? []).map((l) => ({ moeda: l.moeda, valor: Number(l[campo] ?? 0) }));
    // rl0411-2 (VIS-B6): com uma moeda segue texto, para o cartão manter o title e as reticências.
    return linhas.length > 1 ? <ValorPorMoeda linhas={linhas} moedaPadrao={moeda} /> : dinheiro(linhas[0]?.valor ?? d[campo], linhas[0]?.moeda ?? moeda);
  };
  const receber = aba === 'a-receber';
  const podeBaixarLote = s.pode('contas.baixar');
  const podeRenegociar = s.pode('contas.cancelar') && s.pode('contas.criar');
  const escolhidos = useMemo(() => [...marcados.values()], [marcados]);
  const saldoEscolhido = somaPorMoeda(escolhidos, (d) => d.moeda, (d) => d.saldo_centavos);
  const renegociavel = renegociaveisJuntos(escolhidos);
  const selecao = useMemo<SelecaoTabela<Documento> | undefined>(
    () =>
      aba === 'notas-credito' || !(podeBaixarLote || podeRenegociar)
        ? undefined
        : {
            marcados: new Set(marcados.keys()),
            pode: (d) => d.estado === 'em_aberto' || d.estado === 'parcial',
            rotulo: (d) => t('w14.selecionar', { numero: d.numero }),
            rotuloTodas: t('w14.selecionarTodos'),
            aoAlternar: (d, v) =>
              setMarcados((m) => {
                const n = new Map(m);
                if (v) n.set(d.id, d);
                else n.delete(d.id);
                return n;
              }),
            aoAlternarTodas: (linhas, v) =>
              setMarcados((m) => {
                const n = new Map(m);
                for (const d of linhas) {
                  if (v) n.set(d.id, d);
                  else n.delete(d.id);
                }
                return n;
              }),
          },
    [aba, podeBaixarLote, podeRenegociar, marcados, t, chaveFiltro], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const opcoesEstado = [
    { valor: 'abertos', rotulo: t('filtros.abertos') },
    { valor: 'todos', rotulo: t('filtros.todosEstados') },
    ...(['em_aberto', 'parcial', 'pago', 'cancelado', 'perdido', 'renegociado'] as const).filter((x) => (x !== 'perdido' || receber) && (x !== 'renegociado' || aba !== 'notas-credito')).map((x) => ({ valor: x, rotulo: t(`estado.${x}`) })),
  ];

  const colunas: Coluna<Documento>[] = [
    {
      chave: 'numero',
      rotulo: t('campos.numero'),
      ordenavel: true,
      mono: true,
      render: (d) => (
        <span className="flex flex-col">
          <span>{d.numero}</span>
          {d.parcelas > 1 && <span className="text-xs text-tinta-3">{t('detalhe.parcelaDe', { n: d.parcela, total: d.parcelas })}</span>}
        </span>
      ),
    },
    {
      chave: 'parceiro_nome',
      rotulo: aba === 'notas-credito' ? t('campos.parceiro') : receber ? t('campos.cliente') : t('campos.fornecedor'),
      principal: true,
      // Review r16 visual B4: até 16rem; o nome e a descrição longos (renegociação) cortam em vez de empurrar Valor e Estado
      // para a rolagem da tabela a 1440 (a tabela automática ignora o `truncate` sem largura máxima).
      render: (d) => (
        <span className="flex max-w-64 min-w-0 flex-col">
          <span className="truncate font-medium" title={nomeParceiro(d.parceiro_nome)}>{nomeParceiro(d.parceiro_nome)}</span>
          {d.descricao && <span className="truncate text-xs text-tinta-3" title={textoLivre(descricaoGerada(d.descricao), d.parceiro_nome)}>{textoLivre(descricaoGerada(d.descricao), d.parceiro_nome)}</span>}
        </span>
      ),
    },
    ...(aba === 'a-pagar' ? [{ chave: 'tipo', rotulo: t('campos.tipo'), render: (d: Documento) => t(`tipo.${d.tipo}`) } satisfies Coluna<Documento>] : []),
    {
      chave: 'vencimento',
      rotulo: t('campos.vencimento'),
      ordenavel: true,
      mono: true,
      render: (d) =>
        d.atrasado ? (
          <span className="flex flex-col font-medium text-laranja">
            <span>{data(d.vencimento)}</span>
            <span className="text-xs">{t('estado.atrasadoDias', { count: d.dias_atraso })}</span>
          </span>
        ) : (
          data(d.vencimento)
        ),
    },
    { chave: 'emissao', rotulo: t('campos.emissao'), ordenavel: true, oculta: true, mono: true, render: (d) => data(d.emissao) },
    {
      chave: 'saldo_centavos',
      rotulo: t('campos.saldo'),
      ordenavel: true,
      alinhar: 'direita',
      mono: true,
      render: (d) => <span className={d.atrasado ? 'font-medium text-laranja' : undefined}>{anunciarOcultos(dinheiro(d.estado === 'pago' || d.estado === 'cancelado' || d.estado === 'perdido' || d.estado === 'renegociado' ? 0 : d.saldo_centavos, d.moeda))}</span>,
    },
    { chave: 'valor_centavos', rotulo: t('campos.valor'), ordenavel: true, alinhar: 'direita', mono: true, render: (d) => dinheiro(d.valor_centavos, d.moeda) },
    {
      chave: 'estado',
      rotulo: t('campos.estado'),
      render: (d) => (
        <span className="flex flex-wrap gap-1">
          <Selo cor={COR_ESTADO[d.estado]}>{t(`estado.${d.estado}`)}</Selo>
          {d.atrasado && <Selo cor="laranja">{t('estado.atrasado')}</Selo>}
          {d.cobranca?.estado === 'gerada' && <Selo cor="ciano">{t(`cobranca.forma.${d.cobranca.forma ?? 'pix'}`)}</Selo>}
        </span>
      ),
    },
    // Fase 2 (W34): "Aplicar em fatura" na linha da nota de crédito em aberto (secundário: o primário da vista é "Nova nota de crédito").
    ...(aba === 'notas-credito' && podeBaixarLote
      ? [
          {
            chave: 'acoes',
            rotulo: '',
            render: (d: Documento) =>
              d.estado === 'em_aberto' || d.estado === 'parcial' ? (
                <Botao
                  tamanho="sm"
                  icone={<HandCoins className="size-4" />}
                  aria-label={t('w34.aplicarNota', { numero: d.numero })}
                  data-aplicar-nota={d.numero}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAplicarNota(d);
                  }}
                >
                  {t('w34.aplicar')}
                </Botao>
              ) : null,
          } satisfies Coluna<Documento>,
        ]
      : []),
  ];

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <AcaoPrimaria>
        {s.pode('contas.importar') && s.pode('contas.criar') && aba !== 'notas-credito' && (
          <>
            {/* Fase 2 (W18, K3): o modelo também ao lado de "Importar" (antes, só dentro do diálogo). */}
            <BotaoModeloCsv caminho={caminhoImportarTitulos(receber ? 'fatura' : 'conta_pagar')} />
            <Botao icone={<Upload className="size-4" />} onClick={() => setImportar(true)}>
              {t('w7.importar')}
            </Botao>
          </>
        )}
        {s.pode('contas.criar') && (
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setNovo(true)}>
            {t(`acoes.novo.${aba === 'a-receber' ? 'fatura' : aba === 'a-pagar' ? 'conta_pagar' : 'nota_credito'}`)}
          </Botao>
        )}
      </AcaoPrimaria>

      {comCards ? (
        // C1: os 5 cards do antigo, clicáveis (um ativo por vez; clicar no ativo volta ao Total), com a frase embaixo.
        <section aria-label={t('totais.titulo')} className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-cards-documentos="">
          {(
            [
              { card: 'vencidos', rotulo: t('totais.vencidos'), cor: 'perigo', valor: totalEm('atrasado_centavos'), qtd: totais.data?.atrasados },
              { card: 'hoje', rotulo: t('totais.venceHoje'), cor: 'laranja', valor: totalEm('vence_hoje_centavos'), qtd: totais.data?.vence_hoje },
              { card: 'a_vencer', rotulo: t('totais.aVencer'), cor: 'tinta-1', valor: totalEm('a_vencer_centavos'), qtd: totais.data?.a_vencer },
              { card: 'liquidados', rotulo: receber ? t('totais.recebidos') : t('totais.pagos'), cor: 'verde', valor: totalEm('liquidado_centavos'), qtd: totais.data?.liquidados },
              { card: '', rotulo: t('totais.totalPeriodo'), cor: 'ciano', valor: totalEm('total_centavos'), qtd: undefined },
            ] as const
          ).map((c) => (
            <Indicador
              key={c.card || 'total'}
              rotulo={c.rotulo}
              cor={c.cor}
              valor={c.valor}
              detalhe={c.card ? (c.qtd === undefined ? undefined : t('totais.documentos', { count: c.qtd })) : filtrandoTotais ? t('totais.comFiltros') : t('totais.todoPeriodo')}
              ativo={card === c.card}
              aoClicar={() => mudarVarios(urlDoCard(card, c.card))}
              data-card={c.card || 'total'}
            />
          ))}
        </section>
      ) : (
        <section aria-label={t('totais.titulo')} className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Indicador rotulo={t('totais.emAberto')} cor="tinta-1" valor={totalEm('em_aberto_centavos')} detalhe={filtrando ? t('totais.comFiltros') : t('totais.todosEmAberto')} />
          <Indicador rotulo={t('totais.atrasado')} cor="laranja" valor={totalEm('atrasado_centavos')} detalhe={totais.data ? t('totais.documentos', { count: totais.data.atrasados }) : undefined} />
          <Indicador rotulo={t('totais.vence7')} valor={totalEm('vence_em_7_dias_centavos')} />
        </section>
      )}

      {comCards ? (
        // C1: a faixa do antigo: [Vencimento | Competência], período e "Mais filtros (n)" em painel; embaixo, os chips com o
        // "x" e "Limpar filtros". Os filtros seguem na URL e na visão salva, como antes.
        <section aria-label={tn('comum.filtrar')} className="flex min-w-0 flex-col gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <PeriodoComBase bases={['vencimento', 'competencia']} campo={filtro('campo_data')} de={periodo.de} ate={periodo.ate} aoMudar={mudarVarios} />
            <MaisFiltros contagem={contarFiltros([params.parceiro_id, params.conta_id, estadoUrl !== 'abertos', params.categoria_id, params.centro_custo_id, params.valor_min_centavos || params.valor_max_centavos])}>
              <FiltroParceiros parte="campo" papel={receber ? 'cliente' : 'fornecedor'} ids={uuidsDaUrl(filtro('parceiro_id'))} aoMudar={(ids) => mudar('parceiro_id', ids.join(','))} />
              <FiltroContas parte="campo" ids={uuidsDaUrl(filtro('conta_id'))} aoMudar={(ids) => mudar('conta_id', ids.join(','))} />
              <div className="col-span-2 sm:col-span-1">
                <Selecao aria-label={t('campos.estado')} opcoes={opcoesEstado} value={estadoUrl} onChange={(e) => mudar('estado', e.target.value === 'abertos' ? '' : e.target.value)} data-filtro-estado="" />
              </div>
              <FiltrosFinanceiros parte="campo" tipo={receber ? 'receita' : 'despesa'} categorias={uuidsDaUrl(filtro('categoria_id'))} centros={uuidsDaUrl(filtro('centro_custo_id'))} aoMudar={mudar} />
              <FiltroValor min={filtro('valor_min_centavos')} max={filtro('valor_max_centavos')} aoMudar={mudarVarios} />
            </MaisFiltros>
          </div>
          <LinhaChips visivel={filtrando} rotuloLimpar={t('filtros.limpar')} aoLimpar={() => setBusca({}, { replace: true })}>
            <FiltroParceiros parte="chips" papel={receber ? 'cliente' : 'fornecedor'} ids={uuidsDaUrl(filtro('parceiro_id'))} aoMudar={(ids) => mudar('parceiro_id', ids.join(','))} />
            <FiltroContas parte="chips" ids={uuidsDaUrl(filtro('conta_id'))} aoMudar={(ids) => mudar('conta_id', ids.join(','))} />
            <ChipsFixos itens={estadoUrl === 'abertos' ? [] : [{ chave: estadoUrl, rotulo: `${t('campos.estado')}: ${opcoesEstado.find((o) => o.valor === estadoUrl)?.rotulo ?? estadoUrl}` }]} rotulo={t('campos.estado')} aoTirar={() => mudar('estado', '')} data-chips-estado="" />
            <FiltrosFinanceiros parte="chips" tipo={receber ? 'receita' : 'despesa'} categorias={uuidsDaUrl(filtro('categoria_id'))} centros={uuidsDaUrl(filtro('centro_custo_id'))} aoMudar={mudar} />
            <ChipsValor min={filtro('valor_min_centavos')} max={filtro('valor_max_centavos')} aoMudar={mudarVarios} />
          </LinhaChips>
        </section>
      ) : (
      <section aria-label={tn('comum.filtrar')} className="grid grid-cols-2 items-end gap-2 sm:flex sm:flex-wrap">
        <div className="col-span-2 sm:col-span-1 sm:w-56">
          <Selecao
            aria-label={t('campos.estado')}
            opcoes={opcoesEstado}
            value={estadoUrl}
            onChange={(e) => mudar('estado', e.target.value === 'abertos' ? '' : e.target.value)}
          />
        </div>
        <div className="col-span-2 sm:col-span-1 sm:w-56">
          <Selecao aria-label={t('filtros.atraso')} vazio={t('filtros.qualquerVencimento')} opcoes={[{ valor: 'true', rotulo: t('filtros.soAtrasados') }]} value={filtro('atrasados')} onChange={(e) => mudar('atrasados', e.target.value)} />
        </div>
        <FiltroPeriodo bases={['vencimento', 'competencia']} campo={filtro('campo_data')} de={periodo.de} ate={periodo.ate} aoMudar={mudarVarios} />
        {/* Review r26 spec (B1): montado sempre; sem `financeiro.ver`, só os chips do que veio na URL ou na visão salva. */}
        <FiltrosFinanceiros tipo={null} categorias={uuidsDaUrl(filtro('categoria_id'))} centros={uuidsDaUrl(filtro('centro_custo_id'))} aoMudar={mudar} />
        <FiltroValor min={filtro('valor_min_centavos')} max={filtro('valor_max_centavos')} aoMudar={mudarVarios} />
        <FiltroParceiros papel="todos" ids={uuidsDaUrl(filtro('parceiro_id'))} aoMudar={(ids) => mudar('parceiro_id', ids.join(','))} />
        <Botao className="col-span-2 whitespace-nowrap sm:col-span-1" variante="fantasma" icone={<FilterX className="size-4" />} disabled={!filtrando} onClick={() => limparFiltrosEFocarBusca(() => setBusca({}, { replace: true }))} data-limpar-filtros="">
          {t('filtros.limpar')}
        </Botao>
      </section>
      )}

      {selecao && escolhidos.length > 0 && (
        <section aria-label={t('w14.selecionados', { count: escolhidos.length })} className="flex min-w-0 flex-wrap items-center gap-2 rounded-xl border border-borda bg-sup-1 p-3" data-barra-selecao>
          <p className="min-w-0 flex-1 basis-48 text-sm text-tinta-1">
            <span className="font-medium">{t('w14.selecionados', { count: escolhidos.length })}</span>
            <span className="numero text-tinta-2"> · {anunciarOcultos(t('w14.saldoSelecionado', { valor: dinheiroPorMoeda(saldoEscolhido, moeda) }))}</span>
          </p>
          <Botao variante="fantasma" tamanho="sm" icone={<X className="size-4" />} onClick={() => setMarcados(new Map())}>
            {t('w14.limparSelecao')}
          </Botao>
          {podeRenegociar && (
            <Botao tamanho="sm" icone={<Repeat className="size-4" />} disabled={!renegociavel} title={renegociavel ? undefined : t('w14.selecaoMista')} onClick={() => setRenegAberto(true)}>
              {t('w14.renegociarSelecionados')}
            </Botao>
          )}
          {podeBaixarLote && (
            // Review r16 visual M2: um primário por vista ("Nova fatura" no cabeçalho); a ação da seleção é secundária.
            <Botao tamanho="sm" icone={<Banknote className="size-4" />} onClick={() => setLoteAberto(true)}>
              {t(receber ? 'w14.receberSelecionados' : 'w14.pagarSelecionados')}
            </Botao>
          )}
          {podeRenegociar && !renegociavel && <p className="w-full text-xs text-tinta-3">{t('w14.selecaoMista')}</p>}
        </section>
      )}

      <Tabela<Documento>
        tela={`contas.${aba}`}
        caminho="/contas/documentos"
        params={params}
        filtros={{
          estado: filtro('estado'),
          atrasados: filtro('atrasados'),
          card: filtro('card'),
          conta_id: filtro('conta_id'),
          campo_data: filtro('campo_data'),
          de: filtro('de'),
          ate: filtro('ate'),
          parceiro_id: filtro('parceiro_id'),
          categoria_id: filtro('categoria_id'),
          centro_custo_id: filtro('centro_custo_id'),
          valor_min_centavos: filtro('valor_min_centavos'),
          valor_max_centavos: filtro('valor_max_centavos'),
        }}
        aoAplicarFiltros={(f) => setBusca((atual) => filtrosNaUrl(atual, f), { replace: true })}
        colunas={colunas}
        entidade="contas.documento"
        aoClicar={(d) => setAberto(d.id)}
        selecao={selecao}
        buscaNaUrl
        vazio={
          <Vazio
            texto={filtrando ? t('vazio.filtro') : t(`vazio.${aba}`)}
            acao={
              !filtrando && s.pode('contas.criar') ? (
                <Botao icone={<Plus className="size-4" />} onClick={() => setNovo(true)}>
                  {t(`acoes.novo.${aba === 'a-receber' ? 'fatura' : aba === 'a-pagar' ? 'conta_pagar' : 'nota_credito'}`)}
                </Botao>
              ) : undefined
            }
          />
        }
      />

      <FormDocumento aberto={novo} tipos={aba === 'a-receber' ? ['fatura'] : aba === 'a-pagar' ? ['conta_pagar', 'reembolso'] : ['nota_credito']} aoFechar={() => setNovo(false)} aoSalvar={(ids) => ids[0] && setAberto(ids[0])} />
      <DetalheDocumento id={aberto} aoFechar={() => setAberto(null)} />
      <AplicarCredito nota={aplicarNota} aoFechar={() => setAplicarNota(null)} />
      <BaixaLote docs={loteAberto ? escolhidos : null} aoFechar={() => setLoteAberto(false)} aoFeito={() => (setLoteAberto(false), setMarcados(new Map()))} />
      <Renegociar
        docs={renegAberto ? escolhidos : null}
        aoFechar={() => setRenegAberto(false)}
        aoFeito={(ids) => {
          setRenegAberto(false);
          setMarcados(new Map());
          if (ids[0]) setAberto(ids[0]);
        }}
      />
      {aba !== 'notas-credito' && <ImportarTitulos aberto={importar} tipo={receber ? 'fatura' : 'conta_pagar'} aoFechar={() => setImportar(false)} />}
    </div>
  );
}

/**
 * Fase 2 (W36): categoria (do tipo da aba: receita no A receber, despesa no A pagar, as duas nas notas de crédito; inativas
 * também, porque o título antigo continua nelas) e centro de custo. As listas vêm do Financeiro: só com ele e `financeiro.ver`.
 * Review r25 spec (B1): várias categorias e vários centros (`FiltroVarios`: a Selecao acrescenta e os chips mostram), como o antigo.
 * Review r26 spec (B1): sem `financeiro.ver`, a URL (link, visão padrão salva antes de perder a permissão) ainda filtra a
 * lista; então os ids viram chips "Cadastro sem acesso" com o botão de tirar, sem a Selecao e sem pedir as listas.
 */
function FiltrosFinanceiros({ tipo, categorias: escolhidas, centros: escolhidos, aoMudar, parte }: { tipo: 'receita' | 'despesa' | null; categorias: string[]; centros: string[]; aoMudar: (k: string, v: string) => void; parte?: ParteFiltro }) {
  const { t } = useTranslation('contas');
  // Só montado com o Financeiro e `financeiro.ver` (review r25 spec, B2: o `enabled` repete a regra para nunca pedir sem ela).
  const habilitado = useVerFinanceiro();
  // Review r26 robustez (O7): a mesma consulta da árvore inteira (`useCategoriasFin`, até 10.000); antes, uma consulta à
  // parte que parava em 5.000 (25 páginas) sem avisar e repetia o download que o formulário já tinha feito.
  const categorias = useCategoriasFin();
  const centros = useQuery({ queryKey: ['/financeiro/centros-custo', 'todos'], queryFn: () => getTodos<CentroFin>('/financeiro/centros-custo'), staleTime: 30_000, enabled: habilitado });
  const todas = categorias.data?.itens ?? [];
  const opcoes = todas
    .filter((c) => !tipo || c.tipo === tipo)
    .map((c) => ({ valor: c.id, rotulo: caminhoDaCategoria(c, todas) }))
    .sort((a, b) => a.rotulo.localeCompare(b.rotulo));
  return (
    <>
      <FiltroVarios rotulo={t('itens.categoria')} todos={t('filtros.todasCategorias')} opcoes={opcoes} ids={escolhidas} aoMudar={(ids) => aoMudar('categoria_id', ids.join(','))} semAcesso={!habilitado} parte={parte} data-filtro-categorias="" />
      <FiltroVarios rotulo={t('w7.centroCusto')} todos={t('filtros.todosCentros')} opcoes={(centros.data?.itens ?? []).map((c) => ({ valor: c.id, rotulo: c.nome }))} ids={escolhidos} aoMudar={(ids) => aoMudar('centro_custo_id', ids.join(','))} semAcesso={!habilitado} parte={parte} data-filtro-centros="" />
    </>
  );
}

/**
 * C1: contas financeiras (várias), pela conta onde a baixa caiu. Todas as contas, também as inativas (a baixa antiga continua
 * nelas). Sem `financeiro.ver`, só os chips do que veio na URL ou na visão salva, como categoria e centro.
 */
function FiltroContas({ ids, aoMudar, parte }: { ids: string[]; aoMudar: (ids: string[]) => void; parte: ParteFiltro }) {
  const { t } = useTranslation('contas');
  const habilitado = useVerFinanceiro();
  // A mesma chave do `useContas()` das Movimentações (todas as contas): uma consulta só entre as telas.
  const contas = useQuery({ queryKey: ['/financeiro/contas', 'lista', false], queryFn: () => get<Pagina<ContaFin>>('/financeiro/contas', {}), staleTime: 30_000, enabled: habilitado });
  const opcoes = (contas.data?.itens ?? []).map((c) => ({ valor: c.id, rotulo: c.nome }));
  return <FiltroVarios parte={parte} rotulo={t('filtros.contaFinanceira')} todos={t('filtros.todasContas')} opcoes={opcoes} ids={ids} aoMudar={aoMudar} semAcesso={!habilitado} data-filtro-contas="" />;
}
